import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider: React.FC<
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>
> = ({ delayDuration = 150, disableHoverableContent = false, ...props }) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    disableHoverableContent={disableHoverableContent}
    {...props}
  />
)

/**
 * Detects touch-only devices (no hover, coarse pointer) so we can switch
 * the tooltip into tap-to-open mode. SSR-safe: defaults to false and
 * updates after mount.
 */
function useIsTouchDevice() {
  const [isTouch, setIsTouch] = React.useState(false)
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mql = window.matchMedia("(hover: none) and (pointer: coarse)")
    const update = () => setIsTouch(mql.matches)
    update()
    mql.addEventListener?.("change", update)
    return () => mql.removeEventListener?.("change", update)
  }, [])
  return isTouch
}

type TooltipRootProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>

/**
 * On hover-capable devices this is a passthrough of Radix Tooltip.Root.
 * On touch devices (and when the consumer isn't already controlling `open`),
 * it manages open state locally so tapping the trigger opens the tooltip
 * and tapping outside / Esc closes it.
 */
const Tooltip: React.FC<TooltipRootProps> = ({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}) => {
  const isTouch = useIsTouchDevice()
  const isControlled = open !== undefined
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)

  if (!isTouch || isControlled) {
    return (
      <TooltipPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    )
  }

  return (
    <TooltipPrimitive.Root
      open={internalOpen}
      onOpenChange={(next) => {
        setInternalOpen(next)
        onOpenChange?.(next)
      }}
      {...props}
    >
      {children}
    </TooltipPrimitive.Root>
  )
}

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    collisionPadding={collisionPadding}
    className={cn(
      "z-50 overflow-hidden rounded-lg px-3 py-1.5 text-sm",
      "max-w-[min(20rem,calc(100vw-1rem))] whitespace-normal break-words leading-snug",
      "bg-slate-900/95 backdrop-blur-md",
      "border border-white/20",
      "shadow-[0_4px_24px_rgba(0,0,0,0.35)]",
      "text-white",
      "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
